import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { forkJoin } from 'rxjs';
import { CsrWebApiService } from '../../../csr-web-api.service';
import { CriticalFeedbackItem, DashboardAnalyticsResponse, FeedbackReceivedItem } from '../../../models';
import { ChallengeWalkthroughMeasurementsComponent } from './challenge-walkthrough-measurements.component';

type QuickFilterKey = 'day' | 'week' | 'month' | 'custom';

@Component({
  selector: 'app-analytics-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ChallengeWalkthroughMeasurementsComponent,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule
  ],
  templateUrl: './analytics-dashboard.component.html',
  styleUrls: ['./analytics-dashboard.component.scss']
})
export class AnalyticsDashboardComponent implements OnInit {
  analyticsData?: DashboardAnalyticsResponse;
  feedbackRows: FeedbackReceivedItem[] = [];
  feedbackPageIndex = 1;
  readonly feedbackPageSize = 10;
  feedbackTotalCount = 0;
  loading = false;
  activeQuickFilter: QuickFilterKey = 'week';
  readonly appAreaOptions = [
    'onboarding',
    'career_path_finder',
    'skill_gap_analysis',
    'learning_plan',
    'career_challenge',
  ];

  filterForm = new FormGroup({
    fromDate: new FormControl<Date | null>(null),
    toDate: new FormControl<Date | null>(null)
  });

  feedbackFilterForm = new FormGroup({
    appArea: new FormControl<string>(''),
    searchText: new FormControl<string>('')
  });

  constructor(private readonly apiService: CsrWebApiService) {}

  ngOnInit(): void {
    this.filterForm.valueChanges.subscribe(() => {
      this.loadData();
    });
    this.setQuickFilter('week');
  }

  loadData(): void {
    const from = this.filterForm.value.fromDate;
    const to = this.filterForm.value.toDate;
    if (!from || !to) {
      return;
    }

    const fromIso = from.toISOString();
    const toIso = to.toISOString();

    this.loading = true;
    this.feedbackPageIndex = 1;
    forkJoin({
      dashboard: this.apiService.getDashboardAnalytics(fromIso, toIso),
      feedback: this.apiService.getFeedbackReceived(
        this.feedbackPageIndex,
        this.feedbackPageSize,
        fromIso,
        toIso,
        this.feedbackFilterForm.value.appArea || undefined,
        this.feedbackFilterForm.value.searchText || undefined
      )
    }).subscribe({
      next: ({ dashboard, feedback }) => {
        this.analyticsData = dashboard;
        this.feedbackRows = feedback.data;
        this.feedbackTotalCount = feedback.count;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  loadFeedbackPage(nextPage: number): void {
    const totalPages = this.totalFeedbackPages;
    if (nextPage < 1 || nextPage > totalPages) {
      return;
    }

    const from = this.filterForm.value.fromDate;
    const to = this.filterForm.value.toDate;

    this.feedbackPageIndex = nextPage;
    this.apiService
      .getFeedbackReceived(
        this.feedbackPageIndex,
        this.feedbackPageSize,
        from ? from.toISOString() : undefined,
        to ? to.toISOString() : undefined,
        this.feedbackFilterForm.value.appArea || undefined,
        this.feedbackFilterForm.value.searchText || undefined
      )
      .subscribe({
        next: (feedback) => {
          this.feedbackRows = feedback.data;
          this.feedbackTotalCount = feedback.count;
        }
      });
  }

  applyFeedbackFilters(): void {
    this.loadFeedbackPage(1);
  }

  clearFeedbackFilters(): void {
    this.feedbackFilterForm.patchValue({ appArea: '', searchText: '' });
    this.loadFeedbackPage(1);
  }

  get totalFeedbackPages(): number {
    return Math.max(1, Math.ceil(this.feedbackTotalCount / this.feedbackPageSize));
  }

  toFeedbackSignal(row: FeedbackReceivedItem): string {
    if (row.rating !== null && row.rating !== undefined) {
      return `Rating ${row.rating}`;
    }
    if (row.reaction) {
      return `Reaction ${row.reaction}`;
    }
    if (row.choice) {
      return `Choice ${row.choice}`;
    }
    if (row.issueCode) {
      return `Issue ${row.issueCode}`;
    }
    return '-';
  }

  toCriticalReasonLabel(item: CriticalFeedbackItem): string {
    switch (item.reason) {
      case 'critical_issue':
        return item.severity ? `Issue (${item.severity})` : 'Critical issue';
      case 'low_rating':
        return 'Low rating';
      case 'nps_detractor':
        return 'NPS detractor';
      default:
        return item.reason || 'Critical';
    }
  }

  setQuickFilter(key: QuickFilterKey): void {
    this.activeQuickFilter = key;
    if (key === 'custom') {
      return;
    }

    const end = new Date();
    const start = new Date();
    if (key === 'day') {
      start.setHours(0, 0, 0, 0);
    } else if (key === 'week') {
      start.setDate(end.getDate() - 7);
    } else {
      start.setDate(end.getDate() - 30);
    }

    this.filterForm.patchValue({ fromDate: start, toDate: end });
  }

  onCustomRangeOpened(): void {
    this.activeQuickFilter = 'custom';
  }
}
